/**
 * 랜딩 모션. 시계 하나(gsap.ticker)가 Lenis·ScrollTrigger·체류 시계를 모두 돌린다.
 *
 * 체류 시계가 이 페이지의 단일 드라이버다: 스토리 목록의 스크롤 진행률 하나가
 *  - 선이 그려진 길이와 선 머리 위치,
 *  - 시계 숫자(D-3 14:02 → D+1 11:00),
 *  - 정거장 상태(past/active/future)와 휴대폰 화면,
 *  - 밤 톤
 * 을 동시에 정한다. 그래서 이들은 절대 서로 어긋나지 않는다.
 *
 * `startLandingMotion` 은 정리 함수를 돌려준다 — 다른 페이지로 나가면 Lenis·트윈·
 * ScrollTrigger·SplitText 가 전부 원상복구된다.
 */
import { STATIONS } from "@/content/landing";
import { activeStop, formatStay, stopMinutes, stopPosition } from "@/lib/stay-clock";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import Lenis from "lenis";

gsap.registerPlugin(ScrollTrigger, SplitText);

const EASE = "expo.out";
const HEADER_OFFSET = -80;

export const prefersReducedMotion = () =>
  typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

function startSmoothScroll(): () => void {
  if (prefersReducedMotion()) return () => {};
  const lenis = new Lenis({
    lerp: 0.11,
    touchMultiplier: 1.2,
    anchors: { offset: HEADER_OFFSET },
  });
  lenis.on("scroll", ScrollTrigger.update);
  const raf = (time: number) => lenis.raf(time * 1000);
  gsap.ticker.add(raf);
  gsap.ticker.lagSmoothing(0);
  return () => {
    gsap.ticker.remove(raf);
    gsap.ticker.lagSmoothing(500, 33);
    lenis.destroy();
  };
}

/**
 * data-split(줄 단위 마스크), data-reveal(위→아래로 열림), data-draw(헤어라인이 그려짐).
 * data-intro 가 붙으면 스크롤을 기다리지 않고 첫 화면에서 바로, data-delay 만큼 늦춰 재생한다.
 */
function initReveals(root: HTMLElement, intro: boolean) {
  const INTRO_DELAY = 0.05;
  const timing = (el: HTMLElement) => {
    const isIntro = el.hasAttribute("data-intro");
    const delay = Number.parseFloat(el.dataset.delay || "0") + (isIntro ? INTRO_DELAY : 0);
    return { isIntro, delay };
  };

  // 인트로를 건너뛸 때(프리렌더된 첫 화면) 첫 화면 요소는 그대로 둔다 — 이미 보이고 있다.
  const skip = (el: HTMLElement) => !intro && el.hasAttribute("data-intro");

  for (const el of root.querySelectorAll<HTMLElement>("[data-split]")) {
    if (skip(el)) {
      gsap.set(el, { visibility: "visible" });
      continue;
    }
    const { isIntro, delay } = timing(el);
    // autoSplit 은 폰트 로드·리사이즈 때 다시 쪼갠다. 이미 드러난 글은 다시 숨기지 않는다.
    let done = false;
    SplitText.create(el, {
      type: "lines",
      mask: "lines",
      autoSplit: true,
      linesClass: "sl-split-line",
      onSplit(self) {
        gsap.set(el, { visibility: "visible" });
        if (done) return;
        return gsap.from(self.lines, {
          yPercent: 108,
          duration: 1.2,
          stagger: 0.07,
          ease: EASE,
          delay,
          onComplete: () => {
            done = true;
          },
          scrollTrigger: isIntro ? undefined : { trigger: el, start: "top 88%", once: true },
        });
      },
    });
  }

  for (const el of root.querySelectorAll<HTMLElement>("[data-reveal]")) {
    if (skip(el)) {
      gsap.set(el, { visibility: "visible" });
      continue;
    }
    const { isIntro, delay } = timing(el);
    gsap.set(el, { visibility: "visible" });
    gsap.fromTo(
      el,
      { clipPath: "inset(0% 0% 100% 0%)" },
      {
        clipPath: "inset(0% 0% 0% 0%)",
        duration: 1.4,
        ease: EASE,
        delay,
        scrollTrigger: isIntro ? undefined : { trigger: el, start: "top 90%", once: true },
      }
    );
  }

  for (const el of root.querySelectorAll<HTMLElement>("[data-draw]")) {
    if (skip(el)) continue;
    const { isIntro, delay } = timing(el);
    gsap.fromTo(
      el,
      { scaleX: 0 },
      {
        scaleX: 1,
        duration: 1.6,
        ease: EASE,
        delay,
        scrollTrigger: isIntro ? undefined : { trigger: el, start: "top 92%", once: true },
      }
    );
  }

  // 첫 화면의 정거장 이름은 선을 따라 하나씩 떨어진다.
  const stops = root.querySelectorAll<HTMLElement>("[data-strip-stop]");
  if (stops.length && intro) {
    gsap.from(stops, {
      autoAlpha: 0,
      x: -14,
      duration: 0.9,
      ease: EASE,
      stagger: 0.06,
      delay: 0.55,
    });
  }
}

/** 체류 시계 드라이버. 동작 줄이기에서도 돌지만, 상태만 바뀌고 움직임은 CSS 가 끈다. */
function initStayDriver(root: HTMLElement) {
  const story = root.querySelector<HTMLElement>("[data-stay]");
  const list = root.querySelector<HTMLElement>("[data-stay-list]");
  if (!story || !list) return;

  const fills = [...story.querySelectorAll<HTMLElement>("[data-stay-fill]")];
  const heads = [...story.querySelectorAll<HTMLElement>("[data-stay-head]")];
  const days = [...story.querySelectorAll<HTMLElement>("[data-clock-day]")];
  const hours = [...story.querySelectorAll<HTMLElement>("[data-clock-hh]")];
  const mins = [...story.querySelectorAll<HTMLElement>("[data-clock-mm]")];
  const labels = [...story.querySelectorAll<HTMLElement>("[data-clock-label]")];
  const stations = [...story.querySelectorAll<HTMLElement>("[data-stay-station]")];
  const screens = [...story.querySelectorAll<HTMLElement>("[data-stay-screen]")];
  const count = STATIONS.length;

  story.classList.add("js-stay");
  let lastMinutes = Number.NaN;
  let lastActive = -1;

  const setText = (els: HTMLElement[], text: string) => {
    for (const el of els) if (el.textContent !== text) el.textContent = text;
  };

  // 정거장이 바뀔 때 시각을 세어 넘긴다. 동작 줄이기에선 바로 바꾼다.
  const shown = { m: stopMinutes(STATIONS[0]) };
  let countTween: gsap.core.Tween | null = null;
  const render = (minutes: number) => {
    if (minutes === lastMinutes) return;
    lastMinutes = minutes;
    const t = formatStay(minutes);
    setText(days, t.day);
    setText(hours, t.hh);
    setText(mins, t.mm);
  };
  const showClock = (from: number, to: number) => {
    countTween?.kill();
    if (prefersReducedMotion() || from === to) {
      shown.m = to;
      render(to);
      return;
    }
    shown.m = from;
    countTween = gsap.to(shown, {
      m: to,
      duration: 0.9,
      ease: "power3.out",
      onUpdate: () => render(Math.round(shown.m)),
    });
  };

  const apply = (progress: number) => {
    const p = Math.min(1, Math.max(0, progress));
    const pos = stopPosition(p, count);
    const active = Math.min(count - 1, activeStop(pos));

    const height = list.offsetHeight;
    for (const f of fills) gsap.set(f, { scaleY: p });
    for (const h of heads) gsap.set(h, { y: p * height });

    // 시계는 늘 지금 정거장의 시각이다. 정거장이 바뀌면 이전 시각에서 새 시각까지 빠르게
    // 세어 넘어간다 — 스크롤 위치마다 중간 시각을 찍으면 그 시각이 다른 정거장의 이름·화면과
    // 짝지어 읽혀서(“체크아웃 08:34” 에 와이파이 화면) 없는 일이 일어난 것처럼 보인다.
    if (active !== lastActive) {
      const from = lastActive < 0 ? stopMinutes(STATIONS[active]) : stopMinutes(STATIONS[lastActive]);
      lastActive = active;
      showClock(from, stopMinutes(STATIONS[active]));
      setText(labels, STATIONS[active].short);
      stations.forEach((el, i) => {
        el.dataset.state = i < active ? "past" : i === active ? "active" : "future";
      });
      screens.forEach((el, i) => {
        el.dataset.active = String(i === active);
      });
      const tone = STATIONS[active].night ? "night" : "day";
      story.dataset.tone = tone;
      // 헤더도 같은 톤을 따른다 — 밤 위에 반투명 흰 헤더가 회색으로 뜨지 않게.
      document.documentElement.dataset.slTone = tone;
    }
  };

  ScrollTrigger.create({
    trigger: list,
    start: "top center",
    end: "bottom center",
    invalidateOnRefresh: true,
    onUpdate: (self) => apply(self.progress),
    onRefresh: (self) => apply(self.progress),
  });

  // 무대의 휴대폰 화면들은 겹쳐 있고 안 보이는 장은 clip 으로 완전히 가려져 있어서, lazy 로 두면
  // 브라우저가 그 장이 켜지는 순간에야 받기 시작한다(정거장 링크로 건너뛰면 이전 화면이 남아 보인다).
  // 구간에 다가오면 한꺼번에 받아 둔다. 모바일은 무대가 display:none 이라 건드리지 않는다.
  if (matchMedia("(min-width: 768px)").matches) {
    ScrollTrigger.create({
      trigger: story,
      start: "top bottom+=100%",
      once: true,
      onEnter: () => {
        for (const img of story.querySelectorAll<HTMLImageElement>("[data-stay-screen] img")) {
          img.loading = "eager";
        }
      },
    });
  }

  // 화면 밖에서는 신호 링과 콜론 깜빡임을 멈춘다.
  ScrollTrigger.create({
    trigger: story,
    start: "top bottom",
    end: "bottom top",
    onToggle: (self) => story.classList.toggle("is-live", self.isActive),
  });
}

/**
 * 첫 화면의 체류 띠. 화면에 보일 때만 신호 링이 돌고, 데스크톱에서는 "지금" 표식이
 * 정거장을 하나씩 건너가며 그 시각을 켠다 — 스크롤하기 전에도 이 페이지가 시간표라는 걸
 * 보여 주는 대기 상태다. 끝까지 가면 사라졌다가 D-3 에서 다시 시작한다.
 */
function initHeroStrip(root: HTMLElement) {
  const strip = root.querySelector<HTMLElement>("[data-strip]");
  if (!strip) return;
  const stops = [...strip.querySelectorAll<HTMLElement>("[data-strip-stop]")];
  const now = strip.querySelector<HTMLElement>("[data-strip-now]");
  const track = now?.parentElement;
  const desktop = matchMedia("(min-width: 768px)").matches;

  const mark = (i: number) => stops.forEach((s, j) => s.toggleAttribute("data-now", j === i));
  let loop: gsap.core.Timeline | null = null;
  if (desktop && now && track && stops.length > 1) {
    mark(0);
    const HOLD = 1.9;
    loop = gsap.timeline({ repeat: -1, paused: true, delay: 1.2 });
    for (let i = 1; i < stops.length; i++) {
      loop
        .to(
          track,
          { xPercent: (i * 100) / stops.length, duration: 1.1, ease: "expo.inOut" },
          `+=${HOLD}`
        )
        .call(mark, [i], "-=0.35");
    }
    loop
      .to(now, { autoAlpha: 0, duration: 0.5, ease: "power3.out" }, `+=${HOLD}`)
      .set(track, { xPercent: 0 })
      .call(mark, [0])
      .to(now, { autoAlpha: 1, duration: 0.6, ease: "power3.out" });
  }

  strip.classList.add("is-live"); // 첫 화면에 있으니 바로 시작한다
  loop?.play();
  ScrollTrigger.create({
    trigger: strip,
    start: "top bottom",
    end: "bottom top",
    onToggle: (self) => {
      strip.classList.toggle("is-live", self.isActive);
      if (self.isActive) loop?.play();
      else loop?.pause();
    },
  });
}

export function startLandingMotion(root: HTMLElement, { intro = true } = {}): () => void {
  const reduced = prefersReducedMotion();
  const stopScroll = startSmoothScroll();
  const ctx = gsap.context(() => {
    if (!reduced) initReveals(root, intro);
    initStayDriver(root);
    if (!reduced) initHeroStrip(root);
  }, root);

  // 웹폰트가 늦게 오면 줄 바꿈이 달라지므로 위치를 다시 잰다.
  let alive = true;
  document.fonts?.ready.then(() => alive && ScrollTrigger.refresh());

  return () => {
    alive = false;
    delete document.documentElement.dataset.slTone;
    ctx.revert();
    stopScroll();
  };
}
